import ESignatureModal from "./ESignatureModal";
import { useReactToPrint } from "react-to-print";
import { storeMedia } from "../../../api/GeneralApi";
import { uploadW9Form } from "../../../api/Affiliate";
import { toastrOnTopCenter } from "../../../utils/toastr";
import React, { useRef, useState, useEffect } from "react";
import { globalImages } from "../../../utils/portalImages";
import { LEVEL_ADMIN } from "../../../utils/userPermissions";
import { usePermissions } from "../../../context/permissionsContext";
import AuthenticatedLayout from "../../../layouts/AuthenticatedLayout";
import DotPulseBackdrop from "../../shared/components/DotPulseBackdrop";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Header, Page1, Page2, Page3, Page4, Page5, Page6 } from "./w9-from";

function W9Form() {
  const contentRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();
  const w9Data = location.state?.w9Data;
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const isAdmin = hasPermission(LEVEL_ADMIN);
  const formStatus = searchParams?.get("state");
  const [isLoading, setIsLoading] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [openSignModal, setOpenSignModal] = useState(false);
  const [formData, setFormData] = useState({
    ssn: w9Data ? w9Data.ssn : "",
    ein: w9Data ? w9Data.ein : "",
    name: w9Data ? w9Data.name : "",
    address: w9Data ? w9Data.address : "",
    signature: w9Data ? w9Data.signature : "",
    fatca_code: w9Data ? w9Data.fatca_code : "",
    business_name: w9Data ? w9Data.business_name : "",
    signature_text: w9Data ? w9Data.signature_text : "",
    city_state_zip: w9Data ? w9Data.city_state_zip : "",
    account_numbers: w9Data ? w9Data.account_numbers : "",
    exempt_payee_code: w9Data ? w9Data.exempt_payee_code : "",
    has_foreign_partners: w9Data ? w9Data.has_foreign_partners : "",
    signature_font_family: w9Data ? w9Data.signature_font_family : "",
    llc_tax_classification: w9Data ? w9Data.llc_tax_classification : "",
    date: w9Data ? w9Data.created_at : new Date().toISOString().split("T")[0],
    federal_tax_classification: w9Data ? w9Data.federal_tax_classification : "",
    requester_name_and_address: w9Data ? w9Data.requester_name_and_address : "",
    other_federal_tax_classification: w9Data
      ? w9Data.other_federal_tax_classification
      : "",
  });

  const [errors, setErrors] = useState({
    ssn: "",
    ein: "",
    name: "",
    address: "",
    signature: "",
    fatca_code: "",
    business_name: "",
    city_state_zip: "",
    account_numbers: "",
    exempt_payee_code: "",
    llc_tax_classification: "",
    federal_tax_classification: "",
    has_foreign_partners: "",
    requester_name_and_address: "",
    other_federal_tax_classification: "",
  });

  useEffect(() => {
    if (!hasMounted) {
      setHasMounted(true);
      return;
    }

    if (formData.federal_tax_classification !== "other") {
      setFormData((prevData) => ({
        ...prevData,
        other_federal_tax_classification: "",
      }));
    }

    if (formData.federal_tax_classification !== "LLC") {
      setFormData((prevData) => ({
        ...prevData,
        llc_tax_classification: "",
      }));
    }

    if (
      formData.federal_tax_classification === "Individual/sole proprietor" &&
      formData.ein
    ) {
      setFormData((prevData) => ({
        ...prevData,
        ein: "",
      }));
    }

    if (
      formData.federal_tax_classification !== "Individual/sole proprietor" &&
      formData.ssn
    ) {
      setFormData((prevData) => ({
        ...prevData,
        ssn: "",
      }));
    }
  }, [formData]);

  const handleTaxClassificationChange = (label) => {
    setFormData((prevData) => ({
      ...prevData,
      federal_tax_classification:
        prevData.federal_tax_classification === label ? "" : label,
    }));
  };

  const dataURLtoBlob = (dataURL) => {
    const arr = dataURL.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const storeW9Form = async () => {
    let hasError = false;
    let signaturePath = formData.signature;
    const validEIN = /^\d{2}-\d{7}$/.test(formData.ein);
    const validSSN = /^\d{3}-\d{2}-\d{4}$/.test(formData.ssn);

    const newErrors = {
      ssn: "",
      ein: "",
      name: "",
      address: "",
      signature: "",
      fatca_code: "",
      business_name: "",
      city_state_zip: "",
      account_numbers: "",
      exempt_payee_code: "",
      has_foreign_partners: "",
      llc_tax_classification: "",
      federal_tax_classification: "",
      requester_name_and_address: "",
      other_federal_tax_classification: "",
    };

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      hasError = true;
    }

    if (!formData.business_name.trim()) {
      newErrors.business_name = "Business Name is required";
      hasError = true;
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
      hasError = true;
    }

    if (!formData.city_state_zip.trim()) {
      newErrors.city_state_zip = "City State Zip code field is required";
      hasError = true;
    }

    if (!validSSN && !validEIN) {
      newErrors.ssn = "Enter a valid SSN or EIN";
      newErrors.ein = "Enter a valid EIN or SSN";
      hasError = true;
    }

    if (formData?.ssn?.trim() && !validSSN) {
      newErrors.ssn = "SSN format must be XXX-XX-XXXX";
      hasError = true;
    }

    if (formData?.ein?.trim() && !validEIN) {
      newErrors.ein = "EIN format must be XX-XXXXXXX";
      hasError = true;
    }

    if (!formData.federal_tax_classification) {
      newErrors.federal_tax_classification =
        "Check any Federal Tax Classification box";
      hasError = true;
    }

    if (!formData.has_foreign_partners) {
      if (
        formData.federal_tax_classification === "Partnership" ||
        formData.federal_tax_classification === "Trust/estate" ||
        (formData.federal_tax_classification === "LLC" &&
          formData.llc_tax_classification === "P")
      ) {
        newErrors.has_foreign_partners = "Check the box at 3b";
        hasError = true;
      }
    }

    if (
      formData.federal_tax_classification === "LLC" &&
      !["C", "S", "P"].includes(formData.llc_tax_classification?.toUpperCase())
    ) {
      setErrors((prev) => ({
        ...prev,
        llc_tax_classification:
          "Please enter a valid LLC tax classification: C, S, or P.",
      }));
    }

    if (
      formData.federal_tax_classification === "Individual/sole proprietor" &&
      !validSSN
    ) {
      newErrors.ssn = "Valid SSN is required for Individual/sole proprietor";
      hasError = true;
    }

    if (
      formData.federal_tax_classification === "LLC" &&
      !formData.llc_tax_classification
    ) {
      newErrors.llc_tax_classification = "Tax Classification is required";
      hasError = true;
    }

    if (
      formData.federal_tax_classification === "other" &&
      !formData.other_federal_tax_classification
    ) {
      newErrors.other_federal_tax_classification =
        "This field is required when 'Other' is selected";
      hasError = true;
    }

    if (!formData?.signature_text && !formData?.signature) {
      newErrors.signature = "This Signature is required";
      hasError = true;
    }

    if (
      formData.federal_tax_classification === "Individual/sole proprietor" &&
      validSSN
    ) {
      setFormData((prevData) => ({
        ...prevData,
        ein: "",
      }));
    }

    setErrors(newErrors);

    if (hasError) {
      const errorMessages = Object.values(newErrors).filter(
        (msg) => msg !== ""
      );
      const messageToShow =
        errorMessages.length === 1
          ? errorMessages[0]
          : "Please fill in all required fields";
      toastrOnTopCenter(messageToShow, "error");
      return;
    }
    setIsLoading(true);

    if (formData.signature.startsWith("data:image")) {
      const blob = dataURLtoBlob(formData.signature);
      const mediaFormData = new FormData();
      mediaFormData.append("image", blob, "signature.png");
      const uploadResponse = await storeMedia(mediaFormData);
      signaturePath = uploadResponse.data.path;
    }

    try {
      const response = await uploadW9Form({
        ssn: formData.ssn,
        ein: formData.ein,
        name: formData.name,
        signature: signaturePath,
        address: formData.address,
        fatca_code: formData.fatca_code,
        business_name: formData.business_name,
        city_state_zip: formData.city_state_zip,
        signature_text: formData.signature_text,
        account_numbers: formData.account_numbers,
        exempt_payee_code: formData.exempt_payee_code,
        has_foreign_partners: formData.has_foreign_partners,
        signature_font_family: formData.signature_font_family,
        llc_tax_classification: formData.llc_tax_classification,
        requester_name_and_address: formData.requester_name_and_address,
        federal_tax_classification: formData.federal_tax_classification,
        other_federal_tax_classification:
          formData.other_federal_tax_classification,
      });
      toastrOnTopCenter("W-9 form submitted successfully", "success");
      setTimeout(() => navigate("/affiliate-program/initiate-withdraw"), 1000);
    } catch (error) {
      toastrOnTopCenter(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = useReactToPrint({ contentRef });

  return (
    <>
      <Header />
      <body style={{ width: "90%", margin: "0 auto" }} ref={contentRef}>
        <Page1
          errors={errors}
          isAdmin={isAdmin}
          formData={formData}
          setErrors={setErrors}
          formStatus={formStatus}
          setFormData={setFormData}
          setOpenSignModal={setOpenSignModal}
          taxClassification={handleTaxClassificationChange}
        />
        <Page2 />
        <Page3 />
        <Page4 />
        <Page5 />
        <Page6 />
        <script
          dangerouslySetInnerHTML={{
            __html:
              '    const metadata = JSON.parse(document.getElementById("metadata").text);    document.title = metadata.title || metadata.fileName;    const annotations = JSON.parse(document.getElementById("annotations").text);    const pages = document.querySelectorAll(".page");    const createAnnotation = function(container, data, pageNo) {        if (data.type !== "Link" && data.type !== "TextLink") {            return;        }        if (!data.action) {            return;        }        const annotation = document.createElement("div");        annotation.setAttribute("style", "");        annotation.style.left = data.bounds[0] + "px";        annotation.style.top = data.bounds[1] + "px";        annotation.style.width = data.bounds[2] + "px";        annotation.style.height = data.bounds[3] + "px";        annotation.dataset.type = data.type;        if (data.objref) {            annotation.dataset.objref = data.objref;        }        if (data.appearance) {            annotation.style.backgroundImage = "url(\'" + data.appearance + "\')";            annotation.style.backgroundSize = "100% 100%";        }        if (data.action.type === "URI") {            const element = document.createElement("a");            element.href = data.action.uri;            element.title = data.action.uri;            element.target = "_blank";            element.style.position = "absolute";            element.style.width = "100%";            element.style.height = "100%";            annotation.appendChild(element);        } else {            annotation.addEventListener("click", () => {                switch (data.action.type) {                    case "GoTo":                        pages[data.action.page - 1].scrollIntoView();                        break;                    case "Named":                        switch (data.action.name) {                            case "NextPage":                                pages[pageNo - 2].scrollIntoView();                                break;                            case "PrevPage":                                pages[pageNo].scrollIntoView();                                break;                            case "FirstPage":                                pages[0].scrollIntoView();                                break;                            case "LastPage":                                pages[metadata.pagecount - 1].scrollIntoView();                                break;                        }                        break;                }            });        }        container.append(annotation);    };    annotations.pages.forEach(pageAnnotations => {        const pageNo = pageAnnotations.page;        const annotationsContainer = document.createElement("div");        annotationsContainer.className = "annotations-container";        annotationsContainer.style.width = metadata.bounds[pageNo - 1][0];        annotationsContainer.style.height = metadata.bounds[pageNo - 1][1];        pageAnnotations.annotations.forEach(annotation => createAnnotation(annotationsContainer, annotation, pageNo));        pages[pageNo - 1].firstElementChild.appendChild(annotationsContainer);    });',
          }}
        />
      </body>
      {openSignModal && (
        <ESignatureModal
          formData={formData}
          setFormData={setFormData}
          setOpenSignModal={setOpenSignModal}
        />
      )}
      {isLoading && <DotPulseBackdrop />}
      {!isAdmin && (
        <>
          {formStatus === "edit" || formStatus === "create" ? (
            <>
              <div
                onClick={() => navigate("/affiliate-program/initiate-withdraw")}
                className="slide-button btn-danger cancel-side-btn"
              >
                <span className="side-icon text-white">&times;</span>
                <span className="label text-white">Cancel</span>
              </div>
              <div
                className="slide-button btn-success save-side-btn"
                onClick={() => storeW9Form()}
              >
                <span className="side-icon text-white">&#x2713;</span>
                <span className="label">Save</span>
              </div>
            </>
          ) : (
            <div
              onClick={handleDownloadPDF}
              className="slide-button btn-primary download-side-btn"
            >
              <img
                width={16}
                height={16}
                alt="download"
                src={globalImages.download}
              />
              <span className="label text-white">Download</span>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default AuthenticatedLayout(W9Form);
